import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storageApi } from '../utils/api';
import { StorageData } from '../types/storage';
import { format } from 'date-fns';
import { formatPlateForInput, validatePlate } from '../utils/plateFormatter';

interface Props {
  id?: string;
  onClose: () => void;
}

const StorageForm: React.FC<Props> = ({ id, onClose }) => {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [storage, setStorage] = useState<StorageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plakaError, setPlakaError] = useState<string>('');
  const [noError, setNoError] = useState<string>('');
  const [ebatError, setEbatError] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadStorage();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadStorage = async () => {
    try {
      const response = await storageApi.getById(id!);
      if (response.data?.data) {
        setStorage(response.data.data);
      }
    } catch (err) {
      setError('Kayıt yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setError('');
      setNoError('');
      setEbatError('');
      const formData = new FormData(e.currentTarget);
      const plaka = formData.get('plaka') as string;
      const ebat = formData.get('ebat') as string;
      const noValue = formData.get('no') as string;

      if (!validatePlate(plaka)) {
        setPlakaError('Geçerli bir plaka giriniz (Örnek: 34ABC123)');
        return;
      }

      const data = {
        musteri: formData.get('musteri') as string,
        depo: formData.get('depo') as string,
        ebat: ebat.toUpperCase(),
        marka: formData.get('marka') as string,
        adet: parseInt(formData.get('adet') as string),
        arac: formData.get('arac') as string,
        plaka: plaka.toUpperCase(),
        telefon: formData.get('telefon') as string,
        not: formData.get('not') as string,
        tarih: formData.get('tarih') as string,
        no: noValue ? parseInt(noValue) : undefined
      };

      if (id) {
        await storageApi.update(id, data);
      } else {
        await storageApi.create(data);
      }
      onClose();
    } catch (err: any) {
      if (err.response?.data?.error === 'Bu numara zaten kullanımda') {
        setNoError('Bu numara zaten kullanımda');
      } else {
        setError('Kayıt güncellenirken bir hata oluştu');
      }
    }
  };

  const handlePlakaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPlate = formatPlateForInput(e.target.value);
    e.target.value = formattedPlate;
    setPlakaError('');
  };

  const handleEbatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    e.target.value = value;
    setEbatError('');
  };

  if (loading) return <div className="loading">Yükleniyor...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="storage-form">
      <h2>{id ? 'Lastik Kaydını Düzenle' : 'Yeni Lastik Kaydı'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="musteri">Müşteri</label>
          <input
            type="text"
            id="musteri"
            name="musteri"
            defaultValue={storage?.musteri}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="depo">Depo</label>
          <input
            type="text"
            id="depo"
            name="depo"
            defaultValue={storage?.depo}
          />
        </div>
        <div className="form-group">
          <label htmlFor="tarih">Tarih</label>
          <input
            type="date"
            id="tarih"
            name="tarih"
            defaultValue={storage?.tarih ? format(new Date(storage.tarih), 'yyyy-MM-dd') : undefined}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="ebat">Ebat</label>
          <input
            type="text"
            id="ebat"
            name="ebat"
            defaultValue={storage?.ebat}
            onChange={handleEbatChange}
            placeholder="195/65R15"
            maxLength={9}
            required
            style={{ textTransform: 'uppercase' }}
          />
          {ebatError && <div className="error-message" style={{ color: 'red', fontSize: '0.8rem' }}>{ebatError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="marka">Marka</label>
          <input
            type="text"
            id="marka"
            name="marka"
            defaultValue={storage?.marka}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="adet">Adet</label>
          <input
            type="number"
            id="adet"
            name="adet"
            defaultValue={storage?.adet}
            required
            min="1"
          />
        </div>
        <div className="form-group">
          <label htmlFor="arac">Araç</label>
          <input
            type="text"
            id="arac"
            name="arac"
            defaultValue={storage?.arac}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="plaka">Plaka</label>
          <input
            type="text"
            id="plaka"
            name="plaka"
            defaultValue={storage?.plaka}
            onChange={handlePlakaChange}
            maxLength={9}
            placeholder="34ABC123"
            required
            style={{ textTransform: 'uppercase' }}
          />
          {plakaError && <div className="error-message" style={{ color: 'red', fontSize: '0.8rem' }}>{plakaError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="telefon">Telefon</label>
          <input
            type="tel"
            id="telefon"
            name="telefon"
            defaultValue={storage?.telefon}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="not">Not</label>
          <textarea
            id="not"
            name="not"
            defaultValue={storage?.not}
          />
        </div>
        {id && (
          <div className="form-group">
            <label htmlFor="no">No</label>
            <input
              type="text"
              id="no"
              name="no"
              defaultValue={storage?.no?.toString()}
              readOnly
              style={{ backgroundColor: '#f8f9fa' }}
            />
            {noError && <div className="error-message" style={{ color: 'red', fontSize: '0.8rem' }}>{noError}</div>}
          </div>
        )}
        <div className="actions">
          <button type="submit" className="btn btn-primary me-2">
            {id ? 'Güncelle' : 'Kaydet'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
};

export default StorageForm; 